<!-- src/apps/workspace/account/settings/SecurityOverview.vue -->

<script setup lang="ts">
  import AccountCard from '@/apps/workspace/components/account/AccountCard.vue';
  import SettingsLayout from '@/apps/workspace/layouts/SettingsLayout.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useAccount } from '@/shared/composables/useAccount';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { AccountInfo } from '@/types/auth';
  import {
    hasPasswordOf,
    isMfaEnabledOf,
    isPasswordAuthPermittedOf,
    isSsoEnabledOf,
    isWebAuthnEnabledOf,
  } from '@/utils/features';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();

  // Feature toggles — derived from the reactive bootstrap store so they reflect
  // post-login state without re-mounting (e.g. after checkWindowStatus refresh).
  const showSessionsCard = ref(false);
  const mfaFeatureEnabled = computed(() => isMfaEnabledOf(bootstrapStore));
  const webAuthnEnabled = computed(() => isWebAuthnEnabledOf(bootstrapStore));
  const ssoEnabled = computed(() => isSsoEnabledOf(bootstrapStore));
  const hasPw = computed(() => hasPasswordOf(bootstrapStore));
  const passwordAuthPermitted = computed(() => isPasswordAuthPermittedOf(bootstrapStore));
  const { accountInfo, fetchAccountInfo } = useAccount();

  interface SecurityCard {
    id: string;
    icon: { collection: string; name: string };
    title: string;
    description: string;
    status: 'active' | 'inactive' | 'warning';
    statusText: string;
    action: {
      label: string;
      to: string;
    };
  }

  // Helper to build core security cards
  function buildCoreCards(info: AccountInfo): SecurityCard[] {
    return [
      {
        id: 'password',
        icon: { collection: 'heroicons', name: 'lock-closed-solid' },
        title: t('web.auth.change_password.title'),
        description: t('web.settings.password.update_account_password'),
        status: 'active',
        statusText: t('web.settings.security.configured'),
        action: {
          label: t('web.settings.security.change'),
          to: '/account/settings/security/password',
        },
      },
      {
        id: 'mfa',
        icon: { collection: 'heroicons', name: 'key-solid' },
        title: t('web.auth.mfa.title'),
        description: t('web.auth.mfa.setup_description'),
        status: info.mfa_enabled ? 'active' : 'warning',
        statusText: info.mfa_enabled
          ? t('web.auth.account.mfa_enabled')
          : t('web.auth.account.mfa_disabled'),
        action: {
          label: info.mfa_enabled ? t('web.settings.security.manage') : t('web.settings.security.enable'),
          to: '/account/settings/security/mfa',
        },
      },
      {
        id: 'recovery-codes',
        icon: { collection: 'heroicons', name: 'document-text-solid' },
        title: t('web.auth.recovery_codes.title'),
        description: t('web.auth.recovery_codes.description'),
        status: info.recovery_codes_count > 0 ? 'active' : 'inactive',
        statusText:
          info.recovery_codes_count > 0
            ? t('web.settings.security.codes_available', [info.recovery_codes_count])
            : t('web.settings.security.no_codes'),
        action: {
          label: t('web.settings.security.manage'),
          to: '/account/settings/security/recovery-codes',
        },
      },
    ];
  }

  // Helper to build passkey card
  function buildPasskeyCard(info: AccountInfo): SecurityCard {
    const passkeyCount = info.passkeys_count ?? 0;
    return {
      id: 'passkeys',
      icon: { collection: 'heroicons', name: 'finger-print-solid' },
      title: t('web.auth.passkeys.title'),
      description: t('web.auth.passkeys.description'),
      status: passkeyCount > 0 ? 'active' : 'inactive',
      statusText:
        passkeyCount > 0
          ? t('web.auth.passkeys.count', { count: passkeyCount }, passkeyCount)
          : t('web.auth.passkeys.not_configured'),
      action: {
        label: passkeyCount > 0 ? t('web.settings.security.manage') : t('web.settings.security.enable'),
        to: '/account/settings/security/passkeys',
      },
    };
  }

  // Helper to build the Set-password card (#3886), shown to passwordless
  // accounts when policy permits password auth. Routes to the in-app
  // reset-password request page: setting a first password is the same
  // mailbox-proof flow as a reset (emailed link, no current-password step).
  function buildSetPasswordCard(): SecurityCard {
    return {
      id: 'password',
      icon: { collection: 'heroicons', name: 'lock-closed-solid' },
      title: t('web.settings.security.set_password_title'),
      description: t('web.settings.security.set_password_description'),
      status: 'inactive',
      statusText: t('web.settings.security.not_set'),
      action: {
        label: t('web.settings.security.set'),
        to: '/account/settings/security/reset-password',
      },
    };
  }

  // Helper to build connected-identities card (SSO account-linking, #3840).
  // NOT password-dependent — SSO-only accounts are the primary audience.
  function buildConnectionsCard(): SecurityCard {
    return {
      id: 'connections',
      icon: { collection: 'heroicons', name: 'globe-alt-solid' },
      title: t('web.auth.connections.title'),
      description: t('web.auth.connections.description'),
      status: 'active',
      statusText: t('web.auth.connections.overview_status'),
      action: {
        label: t('web.settings.security.manage'),
        to: '/account/settings/security/connections',
      },
    };
  }

  // Helper to build sessions card
  function buildSessionsCard(info: AccountInfo): SecurityCard {
    return {
      id: 'sessions',
      icon: { collection: 'heroicons', name: 'computer-desktop-solid' },
      title: t('web.auth.sessions.title'),
      description: t('web.settings.sessions.manage_active_sessions'),
      status: 'active',
      statusText: t('web.settings.security.active_sessions', [info.active_sessions_count || 1]),
      action: {
        label: t('web.settings.security.manage'),
        to: '/account/settings/security/sessions',
      },
    };
  }

  const PASSWORD_DEPENDENT_CARDS = new Set(['password', 'mfa', 'recovery-codes']);

  const securityCards = computed<SecurityCard[]>(() => {
    if (!accountInfo.value) return [];

    let cards = buildCoreCards(accountInfo.value);

    // Two independent axes (#3886): credential presence (hasPw) picks
    // Change vs Set; policy (passwordAuthPermitted) decides whether a
    // passwordless account gets the Set affordance at all. MFA and recovery
    // codes stay password-dependent either way. Never gated on "is SSO" —
    // hybrid accounts (password + SSO identities) see both surfaces.
    if (!hasPw.value) {
      cards = cards.filter((c) => !PASSWORD_DEPENDENT_CARDS.has(c.id));
      if (passwordAuthPermitted.value) {
        cards.unshift(buildSetPasswordCard());
      }
    }

    // Hide MFA and recovery codes cards when MFA feature is disabled
    if (!mfaFeatureEnabled.value) {
      cards = cards.filter((c) => c.id !== 'mfa' && c.id !== 'recovery-codes');
    }

    if (webAuthnEnabled.value) {
      cards.push(buildPasskeyCard(accountInfo.value));
    }

    // Connected identities card — not password-dependent, so it is added after
    // the SSO-only card filter above and shown whenever SSO is enabled.
    if (ssoEnabled.value) {
      cards.push(buildConnectionsCard());
    }

    if (showSessionsCard.value) {
      cards.push(buildSessionsCard(accountInfo.value));
    }

    return cards;
  });

  /*
   * Status pills, Figma Make "Conta > Segurança": a fully-rounded chip with a
   * tinted ground and a matching 1px ring — the mock's "Configurado" in green.
   * The mock only drew the configured state; inactive/warning reuse the same
   * shape in the palette's other two semantic colours.
   */
  const statusColorClasses = {
    active:
      'bg-emerald-600/[0.08] text-emerald-600 ring-emerald-600/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/25',
    inactive:
      'bg-day-100 text-day-ink-faint ring-day-line-2 dark:bg-night-200 dark:text-night-ink-faint dark:ring-white/[0.13]',
    warning:
      'bg-amber-500/[0.08] text-amber-700 ring-amber-600/25 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/25',
  };

  onMounted(async () => {
    await fetchAccountInfo();
  });
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- SSO empty state — shown when all cards are filtered out. Gated on
           the policy axis too (#3886): this copy means "SSO-enforced, managed
           by your IdP", so it must never show for a passwordless-permitted
           account whose cards are merely absent because the accountInfo
           fetch is pending or failed (securityCards is [] until it loads). -->
      <AccountCard v-if="!hasPw && !passwordAuthPermitted && securityCards.length === 0">
        <div class="flex items-start gap-4 p-[22px]">
          <div
            class="flex size-10 shrink-0 items-center justify-center bg-brand-600/[0.08] dark:bg-brand-500/10">
            <OIcon
              collection="heroicons"
              name="shield-check"
              class="size-5 text-brand-600 dark:text-brand-500"
              aria-hidden="true" />
          </div>
          <div>
            <h3 class="mb-0 font-sans text-[15px] font-semibold text-day-ink dark:text-night-ink">
              {{ t('web.settings.security.sso_managed_title') }}
            </h3>
            <p class="mt-0.5 text-[13px] text-day-ink-faint dark:text-night-ink-faint">
              {{ t('web.settings.security.sso_managed_description') }}
            </p>
          </div>
        </div>
      </AccountCard>

      <!--
        Security cards. The mock lays these out as one narrow column (it only
        had a password card to show); with MFA, recovery codes, passkeys and
        connections all real here, two columns from sm up keeps the tab from
        becoming a scroll.
      -->
      <div
        v-if="securityCards.length > 0"
        class="grid gap-6 sm:grid-cols-2">
        <AccountCard
          v-for="card in securityCards"
          :key="card.id">
          <div class="p-[22px]">
            <div class="mb-3 flex gap-3.5">
              <!-- Square icon tile, mock: 40px on the second surface tint -->
              <div
                class="flex size-10 shrink-0 items-center justify-center bg-day-200 dark:bg-night-200">
                <OIcon
                  :collection="card.icon.collection"
                  :name="card.icon.name"
                  class="size-5 text-day-ink-muted dark:text-night-ink-muted"
                  aria-hidden="true" />
              </div>
              <div class="min-w-0">
                <h3 class="mb-0 font-sans text-[15px] font-semibold text-day-ink dark:text-night-ink">
                  {{ card.title }}
                </h3>
                <p class="mt-0.5 text-[13px] text-day-ink-faint dark:text-night-ink-faint">
                  {{ card.description }}
                </p>
              </div>
            </div>

            <!-- Status Badge -->
            <span
              :class="[
                'inline-flex items-center rounded-full px-3 py-[3px] text-xs ring-1 ring-inset',
                statusColorClasses[card.status],
              ]">
              {{ card.statusText }}
            </span>

            <!-- Action Button -->
            <div class="mt-4">
              <!-- prettier-ignore-attribute class -->
              <router-link
                :to="card.action.to"
                class="
                  inline-flex items-center gap-[7px] text-sm text-brand-600 transition-colors
                  hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400">
                {{ card.action.label }}
                <OIcon
                  collection="heroicons"
                  name="arrow-right-solid"
                  class="size-[15px]"
                  aria-hidden="true" />
              </router-link>
            </div>
          </div>
        </AccountCard>
      </div>
    </div>
  </SettingsLayout>
</template>
